import { z } from "zod";

/**
 * Reto SMG (2026-11-02): un mini examen para después de una exposición. El quiz es la plantilla que
 * se puede editar y reutilizar; cada partida copia sus preguntas, así que cambiarlo no altera los
 * resultados que ya quedaron guardados.
 *
 * Una partida va `en_vivo` (quien expone proyecta y todos responden a la vez, con tiempo) o
 * `a_tu_ritmo` (cada quien avanza solo, sin reloj). Los puntos los calcula siempre el servidor.
 */

export const MODOS_DE_PARTIDA = ["en_vivo", "a_tu_ritmo"] as const;
export type ModoDePartida = (typeof MODOS_DE_PARTIDA)[number];

export const NOMBRE_MODO_DE_PARTIDA: Record<ModoDePartida, string> = {
  en_vivo: "En vivo",
  a_tu_ritmo: "A su ritmo",
};

export const ESTADOS_DE_PARTIDA = ["lobby", "jugando", "terminada"] as const;
export type EstadoDePartida = (typeof ESTADOS_DE_PARTIDA)[number];

/** El avatar de cada jugador: SMG crea sabores, así que se juega con ingredientes. */
export const EMOJIS_DE_JUGADOR = [
  "🌶️",
  "🧀",
  "🍅",
  "🌽",
  "🍋",
  "🥑",
  "🧄",
  "🍯",
  "🌮",
  "🥖",
  "🍕",
  "🍪",
  "🥕",
  "🍇",
  "🥥",
  "🧂",
] as const;

/** Lo que se puede mandar a la pantalla proyectada. */
export const REACCIONES = ["👏", "🔥", "😂", "😮", "💪", "❤️"] as const;

export const TIEMPOS_DE_PREGUNTA = [10, 20, 30, 60] as const;
export const MAXIMO_JUGADORES = 300;
const MINIMO_OPCIONES = 2;
const MAXIMO_OPCIONES = 4;
const MAXIMO_PREGUNTAS = 50;
/** Lo que vale una respuesta correcta a su ritmo, y al instante en vivo. */
const PUNTOS_MAXIMOS = 1000;

const esquemaPreguntaDeQuiz = z.object({
  id: z.string().min(1).max(40),
  texto: z.string().trim().max(300, "Una pregunta no puede pasar de 300 caracteres"),
  opciones: z
    .array(z.string().trim().max(120, "Una opción no puede pasar de 120 caracteres"))
    .max(MAXIMO_OPCIONES),
  /** La posición de la opción correcta. */
  correcta: z.number().int().min(0),
  segundos: z.number().int().min(5).max(120),
});
export type PreguntaDeQuiz = z.infer<typeof esquemaPreguntaDeQuiz>;

export const esquemaGuardarQuiz = z.object({
  id: z.uuid().optional(),
  titulo: z
    .string()
    .trim()
    .min(3, "Ponle un título al reto")
    .max(120, "El título no puede pasar de 120 caracteres"),
  descripcion: z.string().trim().max(500),
  preguntas: z
    .array(esquemaPreguntaDeQuiz)
    .max(MAXIMO_PREGUNTAS, `Un reto no puede tener más de ${MAXIMO_PREGUNTAS} preguntas`),
});
export type GuardarQuiz = z.infer<typeof esquemaGuardarQuiz>;

/** Lo que le falta a un quiz para jugarse. Vacío = se puede jugar. */
export function problemasDeQuiz(preguntas: PreguntaDeQuiz[]): string[] {
  const problemas: string[] = [];
  if (preguntas.length === 0) problemas.push("Agrega al menos una pregunta");
  preguntas.forEach((p, i) => {
    const n = `Pregunta ${i + 1}`;
    if (!p.texto) problemas.push(`${n}: escribe la pregunta`);
    if (p.opciones.length < MINIMO_OPCIONES) problemas.push(`${n}: necesita al menos dos opciones`);
    if (p.opciones.some((o) => !o)) problemas.push(`${n}: hay una opción vacía`);
    if (new Set(p.opciones).size !== p.opciones.length) {
      problemas.push(`${n}: hay opciones repetidas`);
    }
    if (p.correcta >= p.opciones.length) problemas.push(`${n}: marca la respuesta correcta`);
  });
  return problemas;
}

/** En vivo, acertar al instante vale 1000 y acertar en el último segundo vale 500. */
export function puntosDeRespuesta(
  acerto: boolean,
  modo: ModoDePartida,
  tiempoMs: number,
  limiteMs: number,
): number {
  if (!acerto) return 0;
  if (modo === "a_tu_ritmo") return PUNTOS_MAXIMOS;
  const parte = Math.min(Math.max(tiempoMs / limiteMs, 0), 1);
  return Math.round(PUNTOS_MAXIMOS * (1 - parte / 2));
}

export type FaseEnVivo = "pregunta" | "resultado";

/** La pregunta termina al acabarse el tiempo o al contestar todos, sin depender de quien expone. */
export function faseEnVivo(datos: {
  transcurridoMs: number;
  limiteMs: number;
  respondieron: number;
  jugadores: number;
}): FaseEnVivo {
  const todos = datos.jugadores > 0 && datos.respondieron >= datos.jugadores;
  return todos || datos.transcurridoMs >= datos.limiteMs ? "resultado" : "pregunta";
}

/** Cuántas preguntas seguidas lleva acertadas, contando desde la última que contestó. */
export function rachaActual(
  preguntas: { id: string }[],
  respuestas: { preguntaId: string; acerto: boolean }[],
): number {
  let racha = 0;
  for (const p of preguntas) {
    const r = respuestas.find((x) => x.preguntaId === p.id);
    if (!r) continue;
    racha = r.acerto ? racha + 1 : 0;
  }
  return racha;
}

/** "PRO-3" no: solo dígitos, para escribirlo rápido en un teclado de celular. */
export const esquemaCodigoDePartida = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "El código son 6 dígitos");

/** El código de un enlace o QR. El router de la web convierte "933506" en número al leer la URL. */
export function codigoDeEnlace(valor: unknown): string | undefined {
  return esquemaCodigoDePartida.safeParse(String(valor ?? "")).data;
}

export const esquemaNombreDeJugador = z
  .string()
  .trim()
  .min(2, "Escribe tu nombre")
  .max(40, "El nombre no puede pasar de 40 caracteres");

/** Lo que se compara para que dos personas no entren con el mismo nombre. */
export const nombreNormalizado = (nombre: string) =>
  nombre.trim().replace(/\s+/g, " ").toLocaleLowerCase("es");

export const porcentaje = (parte: number, total: number) =>
  total > 0 ? Math.round((parte / total) * 100) : 0;
